import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Plus, Tag } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

// Small, static, editorial seed list per platform — NOT measured
// relevance, NOT AI, NOT a connected data source. Clearly offered as
// "Suggested" starting points the creator can accept or ignore (section
// 7: "Keywords appropriate to each selected platform... populate
// automatically when available").
const PLATFORM_SUGGESTED_KEYWORDS = {
  youtube: ['tutorial', 'how to', 'full video', 'guide', 'explained'],
  tiktok: ['fyp', 'viral', 'trending', 'foryou', 'quick tip'],
  instagram: ['reels', 'explorepage', 'behindthescenes', 'contentcreator'],
  facebook: ['community', 'update', 'watch now', 'creator'],
};

function TagList({ tags, onRemove, testId }) {
  if (!tags.length) return <p className="text-[11px] text-[#555E6E] py-1">None yet.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <Badge key={t} variant="secondary" className="bg-white/10 text-white border-white/10 gap-1 pr-1" data-testid={`${testId}-${t}`}>
          {t}
          <button type="button" onClick={() => onRemove(t)} className="hover:text-red-400" data-testid={`${testId}-remove-${t}`}><X className="h-2.5 w-2.5" /></button>
        </Badge>
      ))}
    </div>
  );
}

function TagAdder({ onAdd, placeholder, testId }) {
  const [value, setValue] = useState('');
  const submit = () => { const v = value.trim(); if (v) { onAdd(v); setValue(''); } };
  return (
    <div className="flex gap-1.5 mt-1.5">
      <Input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} placeholder={placeholder} className="h-7 text-xs bg-black/40 border-white/10" data-testid={testId} />
      <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-white/10 shrink-0" onClick={submit} data-testid={`${testId}-add-button`}><Plus className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

/**
 * Optimize → Keywords: the three approved buckets (section 7). Brand
 * Keywords are genuinely persisted per creator (localStorage, see
 * `lib/optimizeVariants.js`) — the one real "Publishing Metadata" source
 * available today, since `PublishingMetadataDemo.jsx` itself saves
 * nothing. Platform keywords are static editorial suggestions, clearly
 * labeled. Content keywords are manual or AI-suggested (AI Tools panel).
 */
export function OptimizeKeywordsPanel({ variant, onUpdateBucket, brandKeywordLibrary, onAddBrandKeyword, onRemoveBrandKeyword, disabled }) {
  if (disabled) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="optimize-keywords-empty">Select content and a platform first.</p>;

  const platform = variant.platforms[0];
  const suggested = (PLATFORM_SUGGESTED_KEYWORDS[platform] || []).filter((k) => !variant.keywords.platform.includes(k));

  return (
    <div className="space-y-4" data-testid="optimize-keywords-panel">
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Brand Keywords</p>
          <span className="text-[10px] text-[#8A93A6]">Saved to your creator profile</span>
        </div>
        <TagList tags={variant.keywords.brand} onRemove={(k) => onUpdateBucket('brand', variant.keywords.brand.filter((x) => x !== k))} testId="optimize-keyword-brand" />
        <TagAdder onAdd={(k) => { onUpdateBucket('brand', Array.from(new Set([...variant.keywords.brand, k]))); onAddBrandKeyword(k); }} placeholder="Add a reusable brand keyword" testId="optimize-keyword-brand-input" />
        {brandKeywordLibrary.length > 0 && (
          <div className="mt-2">
            <p className="text-[10px] text-[#555E6E] mb-1">From your saved library:</p>
            <div className="flex flex-wrap gap-1.5">
              {brandKeywordLibrary.filter((k) => !variant.keywords.brand.includes(k)).map((k) => (
                <button key={k} onClick={() => onUpdateBucket('brand', [...variant.keywords.brand, k])} className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 text-[#8A93A6] hover:text-white hover:border-white/30" data-testid={`optimize-keyword-brand-library-${k}`}>+ {k}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1">Platform-Specific Keywords <span className="normal-case text-[#555E6E]">({platformLabel(platform)})</span></p>
        <TagList tags={variant.keywords.platform} onRemove={(k) => onUpdateBucket('platform', variant.keywords.platform.filter((x) => x !== k))} testId="optimize-keyword-platform" />
        <TagAdder onAdd={(k) => onUpdateBucket('platform', Array.from(new Set([...variant.keywords.platform, k])))} placeholder="Add a platform keyword" testId="optimize-keyword-platform-input" />
        {suggested.length > 0 && (
          <div className="mt-2">
            <p className="text-[10px] text-[#555E6E] mb-1 flex items-center gap-1"><Tag className="h-2.5 w-2.5" />Suggested for {platformLabel(platform)} — editorial defaults, not measured data:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggested.map((k) => (
                <button key={k} onClick={() => onUpdateBucket('platform', [...variant.keywords.platform, k])} className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-white/15 text-[#8A93A6] hover:text-white hover:border-white/30" data-testid={`optimize-keyword-platform-suggested-${k}`}>+ {k}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1">Current Content Keywords</p>
        <TagList tags={variant.keywords.content} onRemove={(k) => onUpdateBucket('content', variant.keywords.content.filter((x) => x !== k))} testId="optimize-keyword-content" />
        <TagAdder onAdd={(k) => onUpdateBucket('content', Array.from(new Set([...variant.keywords.content, k])))} placeholder="Add a keyword for this specific asset" testId="optimize-keyword-content-input" />
        <p className="text-[10px] text-[#555E6E] mt-1">Or use Generate AI Suggestions (AI Tools) to propose some for review.</p>
      </div>
    </div>
  );
}
